import React, { useEffect, useMemo, useState } from "react";
import { Card, CircularProgress } from "@firecms/ui";
import { useDataSource } from "@firecms/core";

import { fetchUserDisplayNames } from "../firebase/fetchUserDisplayNames";
import { useParticipantProgress } from "../hooks/useParticipantProgress";
import type { EventCollectionId } from "../workspace/collectionIds";
import type { LevelState } from "../types/participantProgress";
import SearchBar from "./SearchBar";
import { useAdminTranslation } from "../i18n/useAdminTranslation";
import { actionTypeLabel } from "../properties/actionTypes";
import {
  statsMuted,
  statsPageTitle,
  statsSectionTitle,
} from "./statsViewTheme";

type ParticipantProgressViewProps = {
  eventId: EventCollectionId;
};

const VISIBLE_LIMIT = 50;

const stateLabels: Record<LevelState, string> = {
  not_started: "Not started",
  in_progress: "In progress",
  completed: "Completed",
};

const stateColors: Record<LevelState, string> = {
  not_started: "bg-surface-300 dark:bg-surface-600",
  in_progress: "bg-sky-500",
  completed: "bg-emerald-500",
};

function modalityLabel(modality: string): string {
  if (modality === "LEVEL_MODALITY_STORY") return "Story";
  if (modality === "LEVEL_MODALITY_QUIZ") return "Quiz";
  if (modality === "LEVEL_MODALITY_TREASURE_HUNT") return "Treasure hunt";
  if (modality === "LEVEL_MODALITY_TOWER_CLIMB") return "Tower climb";
  if (modality === "LEVEL_MODALITY_MAZE") return "Maze";
  return modality;
}

export function ParticipantProgressView({
  eventId,
}: ParticipantProgressViewProps) {
  const { t, locale } = useAdminTranslation();
  const dataSource = useDataSource();
  const [participantSearch, setParticipantSearch] = useState("");
  const [displayNames, setDisplayNames] = useState<Map<string, string>>(
    () => new Map()
  );
  const {
    participants,
    unitSections,
    actionLogs,
    selectedUid,
    setSelectedUid,
    loadingParticipants,
    loadingProgress,
  } = useParticipantProgress(eventId);

  useEffect(() => {
    setDisplayNames(new Map());
    setParticipantSearch("");
  }, [eventId]);

  const filteredParticipants = useMemo(() => {
    if (!participantSearch.trim()) return participants;
    const q = participantSearch.toLowerCase();
    return participants.filter((p) => {
      const name = displayNames.get(p.uid);
      return (
        p.label.toLowerCase().includes(q) ||
        p.uid.toLowerCase().includes(q) ||
        (name != null && name.toLowerCase().includes(q))
      );
    });
  }, [participantSearch, participants, displayNames]);

  const visibleParticipants = useMemo(() => {
    const limited = filteredParticipants.slice(0, VISIBLE_LIMIT);
    if (
      selectedUid &&
      !limited.some((p) => p.uid === selectedUid) &&
      filteredParticipants.some((p) => p.uid === selectedUid)
    ) {
      const selected = filteredParticipants.find((p) => p.uid === selectedUid);
      if (selected) {
        return [selected, ...limited.slice(0, VISIBLE_LIMIT - 1)];
      }
    }
    return limited;
  }, [filteredParticipants, selectedUid]);

  const visibleUidsKey = visibleParticipants.map((p) => p.uid).join(",");

  useEffect(() => {
    if (!visibleUidsKey) return;
    const uids = visibleUidsKey.split(",").filter(Boolean);
    let cancelled = false;
    void fetchUserDisplayNames(dataSource, uids).then((names) => {
      if (cancelled || names.size === 0) return;
      setDisplayNames((prev) => {
        const next = new Map(prev);
        for (const [uid, name] of names) {
          next.set(uid, name);
        }
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [dataSource, visibleUidsKey]);

  const truncated =
    filteredParticipants.length > visibleParticipants.length;

  return (
    <div className="flex h-full w-full bg-surface-50 dark:bg-surface-900">
      <div className="mx-auto w-full max-w-6xl px-4 py-8">
        <h1 className={statsPageTitle}>{t("participantProgress.viewTitle")}</h1>
        <p className={`mt-2 text-sm ${statsMuted}`}>
          {t("participantProgress.viewDescription")}
        </p>

        <div className="mt-6">
          <SearchBar
            onSearch={setParticipantSearch}
            placeholder={t("participantProgress.searchPlaceholder")}
          />
        </div>

        {loadingParticipants && (
          <div className="mt-6 flex justify-center">
            <CircularProgress size="medium" />
          </div>
        )}

        {!loadingParticipants && (
          <div className="mt-4 flex flex-wrap gap-2">
            {visibleParticipants.length === 0 ? (
              <p className={`text-sm ${statsMuted}`}>
                {t("contentInstalls.empty")}
              </p>
            ) : (
              visibleParticipants.map((participant) => (
                <button
                  key={participant.uid}
                  type="button"
                  onClick={() => setSelectedUid(participant.uid)}
                  className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                    selectedUid === participant.uid
                      ? "border-sky-500 bg-sky-500 text-white"
                      : "border-surface-300 bg-white dark:border-surface-600 dark:bg-surface-800"
                  }`}
                >
                  {displayNames.get(participant.uid) ?? participant.label}
                </button>
              ))
            )}
          </div>
        )}

        {!loadingParticipants && truncated && (
          <p className={`mt-3 text-sm ${statsMuted}`}>
            {t("participantProgress.showingLimited", {
              shown: visibleParticipants.length,
              total: filteredParticipants.length,
            })}{" "}
            {t("participantProgress.searchToFindMore")}
          </p>
        )}

        {!selectedUid && !loadingParticipants && participants.length > 0 && (
          <p className={`mt-8 text-sm ${statsMuted}`}>
            {t("participantProgress.selectParticipant")}
          </p>
        )}

        {selectedUid && loadingProgress && (
          <div className="mt-8 flex justify-center">
            <CircularProgress size="medium" />
          </div>
        )}

        {selectedUid && !loadingProgress && (
          <>
            <div className="mt-8 space-y-4">
              {unitSections.map((section) => (
                <Card
                  key={section.unitId}
                  className="border-surface-200 dark:border-surface-700 dark:bg-surface-800"
                >
                  <div className="p-4">
                    <div className={statsSectionTitle}>
                      {section.unitName}{" "}
                      <span className={`text-sm font-normal ${statsMuted}`}>
                        ({section.unitCode})
                      </span>
                    </div>
                    {section.levels.length === 0 ? (
                      <p className={`mt-2 text-sm ${statsMuted}`}>
                        No levels configured for this unit.
                      </p>
                    ) : (
                      <ul className="mt-3 space-y-2">
                        {section.levels.map((level) => (
                          <li
                            key={`${section.unitId}-${level.levelKey}`}
                            className="flex items-center justify-between gap-3 rounded-lg border border-surface-200 px-3 py-2 dark:border-surface-600"
                          >
                            <div className="min-w-0">
                              <div className="truncate text-sm font-medium">
                                {level.storyTitle ?? level.levelCode}
                              </div>
                              <div className={`text-xs ${statsMuted}`}>
                                {modalityLabel(level.modality)} · Level{" "}
                                {level.levelKey}
                              </div>
                            </div>
                            <span
                              className={`shrink-0 rounded-full px-2 py-1 text-xs text-white ${stateColors[level.state]}`}
                            >
                              {stateLabels[level.state]}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Card>
              ))}
            </div>

            <div className="mt-10">
              <h2 className={statsSectionTitle}>
                {t("participantProgress.logsSectionTitle")}
              </h2>
              {actionLogs.length === 0 ? (
                <p className={`mt-2 text-sm ${statsMuted}`}>
                  No actions recorded yet.
                </p>
              ) : (
                <div className="mt-3 overflow-x-auto rounded-lg border border-surface-200 dark:border-surface-700">
                  <table className="min-w-full text-left text-sm">
                    <thead className="bg-surface-100 dark:bg-surface-800">
                      <tr>
                        <th className="px-3 py-2 font-medium">When</th>
                        <th className="px-3 py-2 font-medium">Action</th>
                        <th className="px-3 py-2 font-medium">Level</th>
                        <th className="px-3 py-2 font-medium">Unit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {actionLogs.map((log) => (
                        <tr
                          key={log.id}
                          className="border-t border-surface-200 dark:border-surface-700"
                        >
                          <td className="px-3 py-2 whitespace-nowrap">
                            {log.actionAt.toLocaleString()}
                          </td>
                          <td className="px-3 py-2">
                            {actionTypeLabel(log.actionType, locale)}
                          </td>
                          <td className="px-3 py-2">{log.levelCode ?? "—"}</td>
                          <td className="px-3 py-2">{log.unitCode ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
